import { Controller } from "@hotwired/stimulus"

// Picker autocomplete réutilisable (modale "Associer ou Créer").
// Supporte sélection simple (auto-submit) ou multi-sélection (panier + bouton submit).
//
// Targets côté partial :
//   input, results, hidden, form, footer (optionnel — CTA create), submit (optionnel — bouton multi)
//
// Values :
//   url               : endpoint JSON
//   createUrl         : URL form create (optionnel — active CTA "+ Créer")
//   createQueryParam  : "name" par défaut
//   multi             : false par défaut — true active multi-sélection avec bouton submit
//   hiddenName        : nom du hidden en mode multi (ex: "deal_contact[contact_ids][]")
export default class extends Controller {
  static targets = ["input", "results", "hidden", "form", "footer", "submit"]
  static values = {
    url: String,
    createUrl: { type: String, default: "" },
    createQueryParam: { type: String, default: "name" },
    multi: { type: Boolean, default: false },
    hiddenName: { type: String, default: "" }
  }

  connect() {
    this.debounce = null
    this.activeIndex = -1
    this.results = []
    this.selected = new Map() // id -> { id, label }
    this.updateSubmitVisibility()
    queueMicrotask(() => this.inputTarget?.focus())
  }

  disconnect() {
    if (this.debounce) clearTimeout(this.debounce)
  }

  search() {
    const q = this.inputTarget.value.trim()
    this.activeIndex = -1
    if (q.length < 2) {
      this.renderEmptyState()
      this.updateFooter(q)
      return
    }
    this.setLoading()
    if (this.debounce) clearTimeout(this.debounce)
    this.debounce = setTimeout(() => this.fetchResults(q), 200)
  }

  renderEmptyState() {
    this.resultsTarget.innerHTML = `<div class="entity-picker-empty">Tape au moins 2 lettres…</div>`
  }

  setLoading() {
    this.resultsTarget.innerHTML = `<div class="entity-picker-empty entity-picker-loading">Recherche…</div>`
  }

  async fetchResults(q) {
    try {
      const separator = this.urlValue.includes("?") ? "&" : "?"
      const url = `${this.urlValue}${separator}q=${encodeURIComponent(q)}`
      const response = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.results = await response.json()
      this.renderResults(q)
      this.updateFooter(q)
    } catch (e) {
      this.resultsTarget.innerHTML = `<div class="entity-picker-empty">Erreur de recherche</div>`
    }
  }

  renderResults(query) {
    if (!this.results.length) {
      this.resultsTarget.innerHTML = `<div class="entity-picker-empty">Aucun résultat</div>`
      return
    }
    this.resultsTarget.innerHTML = this.results.map((r, i) => this.renderResultRow(r, i, query)).join("")
  }

  renderResultRow(r, index, query) {
    const label = r.label || r.name || `#${r.id}`
    const subtitle = r.subtitle || ""
    const alreadyLinked = r.meta?.already_linked
    const isSelected = this.selected.has(String(r.id))
    const disabledAttr = alreadyLinked ? "disabled aria-disabled='true'" : ""
    const selectedClass = isSelected ? " is-selected" : ""
    const badge = alreadyLinked
      ? `<span class="entity-picker-badge">Déjà lié</span>`
      : (isSelected ? `<span class="entity-picker-check" aria-hidden="true">✓</span>` : "")
    const subtitleHtml = subtitle ? `<div class="entity-picker-result-subtitle">${this.highlight(subtitle, query)}</div>` : ""
    return `
      <button type="button" class="entity-picker-result${selectedClass}" ${disabledAttr}
              data-action="click->entity-picker#select"
              data-index="${index}" data-id="${r.id}" data-label="${this.escape(label)}">
        <div class="entity-picker-result-body">
          <div class="entity-picker-result-label">${this.highlight(label, query)}</div>
          ${subtitleHtml}
        </div>
        ${badge}
      </button>`
  }

  updateFooter(query) {
    if (!this.hasFooterTarget) return
    if (!this.createUrlValue || query.length < 2) {
      this.footerTarget.innerHTML = ""
      return
    }
    const sep = this.createUrlValue.includes("?") ? "&" : "?"
    const createHref = `${this.createUrlValue}${sep}${this.createQueryParamValue}=${encodeURIComponent(query)}`
    this.footerTarget.innerHTML = `
      <a href="${createHref}" class="entity-picker-create" data-turbo-frame="new_modal">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Créer « ${this.escape(query)} »
      </a>`
  }

  select(event) {
    const btn = event.currentTarget
    if (btn.disabled) return
    const id = String(btn.dataset.id)
    const label = btn.dataset.label

    if (this.multiValue) {
      if (this.selected.has(id)) {
        this.selected.delete(id)
      } else {
        this.selected.set(id, { id, label })
      }
      this.syncHiddenInputs()
      this.updateSubmitVisibility()
      this.updateRowSelectedState(btn)
    } else {
      this.hiddenTarget.value = id
      this.formTarget.requestSubmit()
    }
  }

  syncHiddenInputs() {
    if (!this.multiValue || !this.hasFormTarget) return
    // Retire les anciens hidden générés
    this.formTarget.querySelectorAll("[data-entity-picker-dynamic]").forEach(el => el.remove())
    // Ajoute un hidden par sélection
    this.selected.forEach(({ id }) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.hiddenNameValue
      input.value = id
      input.dataset.entityPickerDynamic = "true"
      this.formTarget.appendChild(input)
    })
  }

  updateSubmitVisibility() {
    if (!this.hasSubmitTarget) return
    const count = this.selected.size
    if (count === 0) {
      this.submitTarget.classList.add("hidden")
      return
    }
    this.submitTarget.classList.remove("hidden")
    const labelEl = this.submitTarget.querySelector("[data-entity-picker-count]")
    if (labelEl) labelEl.textContent = count
  }

  updateRowSelectedState(btn) {
    const id = String(btn.dataset.id)
    btn.classList.toggle("is-selected", this.selected.has(id))
    // Met à jour le badge (checkmark)
    const badgeSpan = btn.querySelector(".entity-picker-check, .entity-picker-badge")
    if (this.selected.has(id)) {
      if (!badgeSpan || !badgeSpan.classList.contains("entity-picker-check")) {
        const existing = btn.querySelector(".entity-picker-check")
        if (!existing) {
          const span = document.createElement("span")
          span.className = "entity-picker-check"
          span.setAttribute("aria-hidden", "true")
          span.textContent = "✓"
          btn.appendChild(span)
        }
      }
    } else {
      btn.querySelector(".entity-picker-check")?.remove()
    }
  }

  async submit(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (!this.multiValue || this.selected.size === 0) return

    const form = this.formTarget
    const formData = new FormData()

    // CSRF
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrf) formData.append("authenticity_token", csrf)

    // Payload : chaque id sélectionné sous le hiddenName donné
    this.selected.forEach(({ id }) => formData.append(this.hiddenNameValue, id))

    try {
      const response = await fetch(form.action, {
        method: (form.method || "post").toUpperCase(),
        body: formData,
        headers: { "Accept": "text/html" },
        redirect: "follow"
      })
      if (response.redirected) {
        window.location.href = response.url
      } else if (response.ok) {
        window.location.reload()
      } else {
        window.location.reload()
      }
    } catch (_) {
      window.location.reload()
    }
  }

  onKeydown(event) {
    const items = this.resultsTarget.querySelectorAll(".entity-picker-result:not([disabled])")
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1)
      this.highlightActive(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this.highlightActive(items)
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this.multiValue && this.selected.size > 0 && this.activeIndex < 0) {
        this.formTarget.requestSubmit()
      } else if (this.activeIndex >= 0 && items[this.activeIndex]) {
        items[this.activeIndex].click()
      } else if (items.length === 1) {
        items[0].click()
      }
    } else if (event.key === "Escape") {
      const modal = this.element.closest("[data-controller~=modal]")
      if (modal) modal.querySelector("[data-action*=modal#close]")?.click()
    }
  }

  highlightActive(items) {
    items.forEach((el, i) => el.classList.toggle("is-active", i === this.activeIndex))
    if (this.activeIndex >= 0) items[this.activeIndex]?.scrollIntoView({ block: "nearest" })
  }

  highlight(text, query) {
    if (!query || query.length < 2) return this.escape(text)
    const escaped = this.escape(text)
    const re = new RegExp(`(${query.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")})`, "ig")
    return escaped.replace(re, "<mark>$1</mark>")
  }

  escape(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }
}
